import React, { useState, useRef } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import MenuNav from './MenuNav/MenuNav'
import { useBp } from '../../../utils/Breakpoints/Breakpoints'
import Modal from '../../common/Modal/Modal'
import { useAuth } from '../../../context/Auth'
import NavItem from './NavItem/NavItem'
import ExternalLink from '../ExternalLink/ExternalLink'
import FaIcon from '../FaIcon/FaIcon'
import DelayRender from '../DelayRender/DelayRender'
import Divider from '../Divider/Divider'

import v from '../../../styles/modules/_variables.scss'
import './Header.scss'

const Header = () => {
  const {
    login,
    currentUser,
    showModal,
    setShowModal,
    destination,
    setDestination,
    clearError,
    incrementError,
    error,
  } = useAuth()

  const [menuOpen, setMenuOpen] = useState(false)
  const [passcode, setPasscode] = useState('')
  const [dirty, setDirty] = useState(false)
  const modalTimer = useRef(null)
  const navigate = useNavigate()

  const aboveXs = useBp('> xs')
  const belowSm = useBp('< sm')

  const autoCloseTimeout = destination ? 5000 : 2000

  const { timing } = v
  const timingMs = Number(timing.slice(0, -1) * 1000)

  const handleSubmit = async () => {
    const p2 = new Promise((resolve, reject) => {
      login(passcode).then(resolve).catch(reject) // Ensure rejection propagates
    })

    p2.then(() => {
      // Only runs if login is successful
      setDirty(false)
      setPasscode('')

      // Set a timeout to match modal animation
      modalTimer.current = setTimeout(() => {
        if (destination) {
          // First close modal
          setShowModal(false)
          // Then navigate after modal animation
          setTimeout(() => {
            navigate(destination.url)
          }, timingMs + 50)
        } else {
          setShowModal(false)
        }
      }, autoCloseTimeout)
    }).catch((error) => {
      // Log and handle the error
      console.error('Error in handleSubmit:', error)
      incrementError()
    })
  }

  const closeModal = () => {
    setShowModal(false)
    // Delay these by the duration of the animation so that
    // the changes are not visible in the UI
    setTimeout(() => {
      clearError()
      setPasscode('')
      setDirty(false)
      destination && setDestination(null)
    }, timingMs)
  }

  const errorHiddenStyles = { height: 0, opacity: 0, marginTop: 0 }
  const modalHiddenStyles = { opacity: 0 }

  return (
    <header className='site-header'>
      <div className='nav-container'>
        <nav className='primary-nav'>
          <NavLink className='btn nav-btn name' draggable='false' end to='/'>
            Jason Cardarelli
          </NavLink>
          {aboveXs && (
            <>
              <NavLink className='btn nav-btn' draggable='false' to='/work'>
                Work
              </NavLink>
              <NavLink className='btn nav-btn' draggable='false' to='/about'>
                About
              </NavLink>
            </>
          )}
        </nav>
        <div className='nav-group'>
          {!currentUser && (
            <>
              <NavItem
                id='auth'
                className='secondary'
                type='hybrid'
                text='Unlock'
                tooltip
                iconBp={belowSm}
                action={() => setShowModal(true)}
                icon={{ name: 'lock-open' }}
              />
            </>
          )}
          {belowSm && <MenuNav menuOpen={menuOpen} setMenuOpen={setMenuOpen} />}
        </div>
      </div>
      {currentUser ? (
        <DelayRender
          selector='.modal'
          showCondition={showModal}
          hiddenStyles={modalHiddenStyles}>
          <Modal
            autoCloseTimeout={autoCloseTimeout}
            dirty={dirty}
            modalTimer={modalTimer.current}
            title='Portfolio unlocked'
            customClass={destination ? 'redirect' : ''}
            closeModal={() => {
              closeModal()
            }}>
            {destination ? (
              <p className='destination-message'>
                Continuing to{' '}
                <button
                  className='text-link underline'
                  onClick={() => {
                    // First close modal
                    setShowModal(false)
                    // Then navigate after modal animation
                    setTimeout(() => {
                      navigate(destination.url)
                    }, timingMs + 50)
                  }}>
                  {destination.name}
                </button>
                .
              </p>
            ) : (
              <p>Successfully unlocked the portfolio.</p>
            )}
          </Modal>
        </DelayRender>
      ) : (
        <DelayRender
          selector='.modal'
          showCondition={showModal}
          hiddenStyles={modalHiddenStyles}>
          <Modal
            dirty={dirty}
            modalTimer={modalTimer.current}
            error={error}
            title={destination ? 'Passcode required' : 'Unlock portfolio'}
            closeModal={() => {
              closeModal()
            }}>
            <>
              <p>
                Find the passcode in the <span>header of my resume</span> to
                access work samples and additional features.
              </p>
              <form onSubmit={handleSubmit}>
                <div className='input-group'>
                  <input
                    name='passcode'
                    type='password'
                    placeholder='Enter passcode'
                    value={passcode}
                    onChange={(e) => {
                      setPasscode(e.target.value)
                      setTimeout(() => {
                        setDirty(e.target.value.length > 0)
                      }, 500)
                      error.active && clearError()
                    }}
                  />
                  <DelayRender
                    selector='.error-container'
                    showCondition={error.active}
                    hiddenStyles={errorHiddenStyles}>
                    <div className='error-container'>
                      <FaIcon name='triangle-exclamation' />
                      <span className='error'>
                        {dirty ? 'Invalid passcode.' : 'Passcode is required.'}
                      </span>
                    </div>
                  </DelayRender>
                </div>
                <button className='btn primary' type='submit'>
                  {destination ? 'Continue' : 'Submit'}
                </button>
              </form>
              <Divider type='horizontal' />
              <p>
                To request the passcode, contact me:{' '}
                <ExternalLink
                  label='jason@jasoncardarelli.com'
                  url='mailto:jason@jasoncardarelli.com?subject=Portfolio inquiry'
                />
              </p>
            </>
          </Modal>
        </DelayRender>
      )}
    </header>
  )
}

export default Header
